import { hexToRgb, type Rgb } from './hex';

/**
 * WCAG 2.x contrast maths. Pure.
 *
 * This is a hard gate rather than a nicety: an unreadable amount field is a financial risk. A player
 * who cannot read what they are about to pay either pays the wrong amount or abandons.
 */

/** WCAG AA for body text and for any text carrying meaning about money. */
export const MIN_TEXT_CONTRAST = 4.5;

/** WCAG AA for boundaries and focus indicators — non-text contrast, 1.4.11. */
export const MIN_BOUNDARY_CONTRAST = 3;

function linearize(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(rgb: Rgb): number {
  return 0.2126 * linearize(rgb.r) + 0.7152 * linearize(rgb.g) + 0.0722 * linearize(rgb.b);
}

export function contrastRatio(hexA: string, hexB: string): number {
  const la = relativeLuminance(hexToRgb(hexA));
  const lb = relativeLuminance(hexToRgb(hexB));
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export function meetsContrast(hexA: string, hexB: string, minimum: number): boolean {
  return contrastRatio(hexA, hexB) >= minimum;
}

/**
 * Returns whichever candidate scores highest against `background`.
 *
 * Used for `--vp-on-primary` (black or white over the merchant's primary) and for replacing an
 * unreadable `text`. Picking the best rather than the first passing candidate means a marginal
 * palette lands on the more legible option rather than the one that merely scraped through.
 */
export function pickMostReadable(background: string, candidates: readonly string[]): string {
  let best = candidates[0];
  if (best === undefined) {
    throw new Error('pickMostReadable requires at least one candidate');
  }
  let bestRatio = contrastRatio(background, best);

  for (const candidate of candidates.slice(1)) {
    const ratio = contrastRatio(background, candidate);
    if (ratio > bestRatio) {
      best = candidate;
      bestRatio = ratio;
    }
  }
  return best;
}
